import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Heart, 
  Music,
  Play,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FavoritesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API}/favorites`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (songId) => {
    try {
      const response = await fetch(`${API}/favorites/${songId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setFavorites(prev => prev.filter(s => s.song_id !== songId));
        toast.success('Eliminado de favoritos');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'bg-key-c', 'G': 'bg-key-g', 'D': 'bg-key-d',
      'A': 'bg-key-a', 'E': 'bg-key-e', 'B': 'bg-key-b',
      'F#': 'bg-key-fsharp', 'Db': 'bg-key-db', 'Ab': 'bg-key-ab',
      'Eb': 'bg-key-eb', 'Bb': 'bg-key-bb', 'F': 'bg-key-f'
    };
    const baseKey = key?.replace('m', '');
    return keyMap[baseKey] || 'bg-primary';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="favorites-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <Heart className="w-8 h-8 text-destructive" />
          Mis Favoritos
        </h1>
        <p className="text-muted-foreground mt-1">
          {favorites.length} canciones guardadas
        </p>
      </div>

      {/* Favorites Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : favorites.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {favorites.map((song, index) => (
            <Card
              key={song.song_id}
              className="border-border/50 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`favorite-card-${song.song_id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className={`key-badge ${getKeyBadgeClass(song.original_key)}`}>
                    {song.original_key}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => handleRemoveFavorite(song.song_id)}
                    data-testid={`remove-favorite-${song.song_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <Link to={`/songs/${song.song_id}`}>
                  <h3 className="font-heading font-semibold text-lg mb-2 hover:text-primary transition-colors line-clamp-1">
                    {song.title}
                  </h3>
                </Link>
                
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {song.lyrics?.split('\n')[0]}
                </p>
                
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {song.theme && <Badge variant="secondary">{song.theme}</Badge>}
                  {song.bpm && <Badge variant="outline">{song.bpm} BPM</Badge>}
                </div>
                
                <Link to={`/singer?song=${song.song_id}`}>
                  <Button variant="outline" size="sm" className="w-full gap-1">
                    <Play className="w-4 h-4" />
                    Presentar
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-xl font-medium mb-2">No tienes favoritos aún</h3>
          <p className="text-muted-foreground mb-4">
            Agrega canciones a tus favoritos desde la biblioteca
          </p>
          <Link to="/songs">
            <Button>
              <Music className="w-4 h-4 mr-2" />
              Explorar canciones
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;
